import styled from 'styled-components';
import { getColor } from 'utils';
import constants from 'constants/index';
import { Body } from 'components/typography';

const light = constants.THEME.LIGHT;
const isLight = props => props.$selectedTheme === light;
const PAGE_RADIUS = '24px';

export const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.85rem, 2.4vw, 2.85rem);
  line-height: 1.04;
  letter-spacing: -0.04em;
  font-weight: 700;
  color: var(--pm-text-primary);
  overflow-wrap: anywhere;
`;

export const Sidebar = styled.aside`
  position: relative;
  min-width: 0;
`;

export const PageWrapper = styled.div`
  --pm-page-bg: 'transparent';

  --pm-surface-border: ${props => (
    isLight(props)
      ? '1px solid rgba(148, 163, 184, 0.18)'
      : '1px solid rgba(148, 163, 184, 0.10)'
  )};

  --pm-surface-bg: ${props => (
    isLight(props)
      ? 'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))'
      : `linear-gradient(
          180deg,
          rgba(255, 255, 255, 0.05),
          rgba(255, 255, 255, 0.02)
        ), rgba(10, 16, 24, 0.92)`
  )};

  --pm-surface-shadow: ${props => (
    isLight(props)
      ? `0 20px 50px rgba(15, 23, 42, 0.08),
         inset 0 1px 0 rgba(255,255,255,0.75)`
      : `0 30px 80px rgba(0, 0, 0, 0.45),
         inset 0 1px 0 rgba(255, 255, 255, 0.06)`
  )};

  --pm-card-bg: ${props => (
    isLight(props)
      ? 'rgba(255, 255, 255, 0.82)'
      : 'rgba(255, 255, 255, 0.03)'
  )};

  --pm-card-bg-strong: ${props => (
    isLight(props)
      ? 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(248,250,252,0.88))'
      : `linear-gradient(
          180deg,
          rgba(255, 255, 255, 0.045),
          rgba(255, 255, 255, 0.025)
        )`
  )};

  --pm-stage-bg: ${props => (
    isLight(props)
      ? `radial-gradient(
          circle at top,
          rgba(59, 130, 246, 0.12),
          transparent 42%
        ),
        linear-gradient(
          180deg,
          rgba(255,255,255,0.95),
          rgba(241,245,249,0.92)
        )`
      : `radial-gradient(
          circle at top,
          rgba(60, 168, 255, 0.2),
          transparent 38%
        ),
        linear-gradient(
          180deg,
          rgba(255,255,255,0.08),
          rgba(255,255,255,0.03)
        )`
  )};

  --pm-stage-border: ${props => (
    isLight(props)
      ? 'rgba(148, 163, 184, 0.18)'
      : 'rgba(255, 255, 255, 0.06)'
  )};

  --pm-overlay: ${props => (
    isLight(props)
      ? `linear-gradient(
          180deg,
          rgba(248, 250, 252, 0.18) 0%,
          transparent 28%,
          transparent 72%,
          rgba(226, 232, 240, 0.26) 100%
        )`
      : `linear-gradient(
          180deg,
          rgba(8, 11, 18, 0.24) 0%,
          transparent 28%,
          transparent 72%,
          rgba(8, 11, 18, 0.42) 100%
        )`
  )};

  --pm-text-primary: ${props => getColor(props, 'onPrimary')};

  --pm-text-secondary: ${props => (
    isLight(props)
      ? 'rgba(15, 23, 42, 0.74)'
      : 'rgba(241, 248, 255, 0.9)'
  )};

  --pm-text-muted: ${props => (
   isLight(props)
    ? 'rgba(71, 85, 105, 0.82)'
    : 'rgba(240, 247, 252, 0.68)'
  )};

  --pm-text-soft: ${props => (
    isLight(props)
      ? 'rgba(71, 85, 105, 0.82)'
      : 'rgba(240, 247, 252, 0.88)'
  )};

  --pm-text-faint: ${props => (
    isLight(props)
      ? 'rgba(100, 116, 139, 0.82)'
      : 'rgba(240, 247, 252, 0.55)'
  )};

  --pm-chip-bg: ${props => (
    isLight(props)
      ? 'rgba(37, 99, 235, 0.08)'
      : 'rgba(255, 255, 255, 0.05)'
  )};

  --pm-chip-border: ${props => (
    isLight(props)
      ? 'rgba(37, 99, 235, 0.14)'
      : 'rgba(255, 255, 255, 0.08)'
  )};

  --pm-chip-text: ${props => (
    isLight(props)
      ? '#1e3a8a'
      : 'rgba(255, 255, 255, 0.82)'
  )};

  --pm-accent-soft: ${props => (
    isLight(props)
      ? 'rgba(37, 99, 235, 0.10)'
      : 'rgba(92, 120, 255, 0.14)'
  )};

  --pm-accent-border: ${props => (
    isLight(props)
      ? 'rgba(37, 99, 235, 0.18)'
      : 'rgba(60, 168, 255, 0.28)'
  )};

  --pm-accent-text: ${props => (
    isLight(props)
      ? '#1d4ed8'
      : '#bde7ff'
  )};

  --pm-accent-hover: ${props => (
    isLight(props)
      ? '#1e40af'
      : '#ecf8ff'
  )};

  --pm-button-primary-bg: ${props => (
    isLight(props)
      ? 'linear-gradient(180deg, #4f8cff 0%, #2563eb 100%)'
      : `linear-gradient(
          180deg,
          rgba(74, 181, 255, 0.96) 0%,
          rgba(39, 137, 255, 0.96) 100%
        )`
  )};

  --pm-button-secondary-bg: ${props => (
    isLight(props)
      ? 'rgba(255,255,255,0.88)'
      : 'rgba(255, 255, 255, 0.04)'
  )};

  --pm-divider: ${props => (
    isLight(props)
      ? `linear-gradient(
          90deg,
          rgba(148,163,184,0),
          rgba(148,163,184,0.28),
          rgba(148,163,184,0)
        )`
      : `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 0.12),
          rgba(255, 255, 255, 0)
        )`
  )};

  --pm-placeholder-bg: ${props => (
    isLight(props)
      ? 'rgba(248, 250, 252, 0.92)'
      : 'rgba(255, 255, 255, 0.02)'
  )};

  --pm-placeholder-border: ${props => (
    isLight(props)
      ? 'rgba(148, 163, 184, 0.28)'
      : 'rgba(255, 255, 255, 0.12)'
  )};

  --pm-popup-bg: ${props => (
    isLight(props)
      ? `linear-gradient(
          180deg,
          rgba(255,255,255,0.98),
          rgba(248,250,252,0.98)
        )`
      : `linear-gradient(
          180deg,
          rgba(18, 25, 36, 0.96),
          rgba(10, 16, 24, 0.98)
        )`
  )};

  --pm-popup-shadow: ${props => (
    isLight(props)
      ? '0 20px 40px rgba(15, 23, 42, 0.12)'
      : '0 24px 48px rgba(0, 0, 0, 0.34)'
  )};

  height: 100dvh;
  overflow-y: auto;
  background: var(--pm-page-bg);

  @media (max-width: 700px) {
    padding: 32px;
  }
`;

export const PreviewBody = styled(Body)`
  color: var(--pm-text-secondary);
  width: auto;
`;

export const PreviewLabelText = styled(Body)`
  color: var(--pm-text-muted);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  width: auto;
`;

export const PageShell = styled.div`
  max-width: 1560px;
  margin: 0 auto;
  padding-top: 16px;
`;

export const StatusCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media (max-width: 980px) {
    justify-content: flex-start;
  }
`;


export const StatusButton = styled.button`
  cursor: pointer;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: var(--pm-surface-border);
  background-color: transparent; /* Clear background */
  color: #983995;
  font-size: 13px;
  font-weight: 600;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

   &:hover {
    filter: brightness(1.15);
    background-color: #bdbdbd;
    border: 1px solid #983995;
  }
`;

export const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  background: ${props => (props.$accent ? 'var(--pm-accent-soft)' : 'var(--pm-chip-bg)')};
  border: 1px solid ${props => (props.$accent ? 'var(--pm-accent-border)' : 'var(--pm-chip-border)')};
  color: ${props => (props.$accent ? 'var(--pm-accent-text)' : 'var(--pm-chip-text)')};
  font-size: 13px;
  font-weight: 600;
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 390px);
  gap: 24px;
  align-items: start;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

export const ViewerColumn = styled.div`
  min-width: 0;
`;

export const Surface = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${PAGE_RADIUS};
  border: var(--pm-surface-border);
  background: var(--pm-surface-bg);
  box-shadow: var(--pm-surface-shadow);
  backdrop-filter: blur(14px);
`;

export const ViewerSurface = styled(Surface)`
  padding: 18px;
`;

export const ViewerTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ViewerHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

export const ViewerLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--pm-text-muted);
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const ViewerMetaGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
`;

export const ViewerStage = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid var(--pm-stage-border);
  min-height: clamp(380px, 65vh, 760px);
  background: var(--pm-stage-bg);

  @media (max-width: 640px) {
    min-height: 340px;
  }
`;

export const ViewerStageInner = styled.div`
  position: absolute;
  inset: 0;
`;

export const ViewerPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
  color: var(--pm-text-secondary);
  font-size: 15px;
  line-height: 1.6;
`;

export const StageOverlay = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: var(--pm-overlay);
`;

export const StageBadgeRow = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

export const StageFooter = styled.div`
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const StageFooterText = styled.div`
  color: var(--pm-text-soft);
  font-size: 14px;
  font-weight: 600;
`;

export const StageFooterSubtext = styled.div`
  margin-top: 4px;
  color: var(--pm-text-faint);
  font-size: 12px;
`;

export const TabsCard = styled(Surface)`
  margin-top: 18px;
  padding: 18px;
`;

export const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
`;

export const TabButton = styled.button`
  min-height: 42px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? 'var(--pm-accent-border)' : 'var(--pm-chip-border)')};
  background: ${props => (props.$active ? 'var(--pm-accent-soft)' : 'var(--pm-chip-bg)')};
  color: ${props => (props.$active ? 'var(--pm-accent-text)' : 'var(--pm-chip-text)')};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: var(--pm-accent-border);
    color: var(--pm-accent-hover);
  }
`;

export const InfoCard = styled.div`
  min-height: 100%;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid var(--pm-stage-border);
  background: var(--pm-card-bg);
`;

export const OverviewCard = styled(InfoCard)`
  padding: 22px;
`;

export const OverviewMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const OverviewMetaItem = styled.div`
  min-height: 84px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid var(--pm-stage-border);
  background: var(--pm-card-bg-strong);
`;

export const OverviewDivider = styled.div`
  height: 1px;
  margin: 18px 0 16px;
  background: var(--pm-divider);
`;

export const OverviewTagsSection = styled.div`
  display: grid;
  gap: 12px;
`;

export const SectionEyebrow = styled.div`
  color: var(--pm-text-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const TabPanel = styled.div`
  display: grid;
  gap: 18px;
`;

export const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }
`;

export const InfoTitle = styled.h3`
  margin: 0 0 12px;
  color: var(--pm-text-primary);
  font-size: 15px;
  font-weight: 700;
`;

export const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--pm-chip-bg);
  border: 1px solid var(--pm-chip-border);
  color: var(--pm-chip-text);
  font-size: 13px;
  font-weight: 600;
`;

export const FormatTag = styled(Tag)`
  cursor: pointer;
  background: var(--pm-accent-soft);
  border-color: var(--pm-accent-border);
  color: var(--pm-accent-text);
`;

export const EmptyState = styled.div`
  border-radius: 20px;
  border: 1px dashed var(--pm-placeholder-border);
  padding: 20px;
  color: var(--pm-text-faint);
  font-size: 14px;
  line-height: 1.7;
  background: var(--pm-placeholder-bg);
`;

export const SidebarInner = styled(Surface)`
  position: sticky;
  top: 24px;
  padding: 18px;

  @media (max-width: 1180px) {
    position: static;
  }
`;

export const SidebarImageBox = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 20px;
  border: 1px solid var(--pm-stage-border);
  background: var(--pm-stage-bg);
`;

export const SidebarImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

export const SummaryItem = styled.div`
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid var(--pm-stage-border);
  background: var(--pm-card-bg);
`;

export const SidebarImagePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: var(--pm-text-secondary);
  font-size: 14px;
  line-height: 1.6;
`;

export const SidebarHeader = styled.div`
  margin-top: 18px;
`;

export const Overline = styled.div`
  margin-bottom: 10px;
  color: var(--pm-text-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CreatorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: var(--pm-chip-bg);
  border: var(--pm-surface-border);
  color: var(--pm-text-secondary);
  font-size: 14px;
  font-weight: 600;
`;

export const AccentAvatar = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, #7fd7ff 0%, #3ca8ff 100%);
  box-shadow: 0 0 18px rgba(60, 168, 255, 0.35);
`;

export const ButtonStack = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 18px;
`;

export const PrimeActionButton = styled('div')`
  font-family: "Poppins-Medium";
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  border-radius: 30px;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background-color: #983995;
  cursor: pointer;
  color: ${props => getColor(props, 'white')};
  &:hover {
    filter: brightness(1.15);
    background-color: #5D225B;
  }
  margin: 0 auto;
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  border: 1px solid ${props => (props.$primary ? 'var(--pm-accent-border)' : 'var(--pm-chip-border)')};
  border-radius: 30px;
  padding: 0 16px;
  background: ${props => (props.$primary ? 'var(--pm-button-primary-bg)' : 'var(--pm-button-secondary-bg)')};
  color: ${props => (props.$primary ? '#ffffff' : 'var(--pm-text-primary)')};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${props => (props.$primary ? '0 18px 35px rgba(37, 99, 235, 0.22)' : 'none')};
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--pm-accent-border);
    box-shadow: ${props => (props.$primary
      ? '0 22px 38px rgba(37, 99, 235, 0.28)'
      : '0 12px 24px rgba(15, 23, 42, 0.10)')};
  }
`;

export const ActionsInline = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const Divider = styled.div`
  height: 1px;
  margin: 18px 0;
  background: var(--pm-divider);
`;

export const SectionTitle = styled.h3`
  margin: 0 0 14px;
  color: var(--pm-text-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

export const StatCard = styled.div`
  min-height: 92px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid var(--pm-stage-border);
  background: var(--pm-card-bg);
`;

export const StatLabel = styled.div`
  color: var(--pm-text-muted);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const StatValue = styled.div`
  margin-top: 10px;
  color: var(--pm-text-primary);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.03em;
`;

export const SummaryGrid = styled.div`
  display: grid;
  gap: 12px;
`;

export const SummaryValue = styled.div`
  color: var(--pm-text-secondary);
  font-size: 14px;
  font-weight: 600;
  word-break: break-word;
`;

export const ThumbnailBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  background: var(--pm-accent-soft);
  border: 1px solid var(--pm-accent-border);
  color: var(--pm-accent-text);
  font-size: 13px;
  font-weight: 600;
`;

export const DownloadMenuWrap = styled.div`
  position: relative;
`;

export const DownloadPopup = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  right: 0;
  z-index: 30;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid var(--pm-chip-border);
  background: var(--pm-popup-bg);
  box-shadow: var(--pm-popup-shadow);
  backdrop-filter: blur(14px);
`;

export const DownloadPopupTitle = styled.div`
  margin-bottom: 12px;
  color: var(--pm-text-primary);
  font-size: 14px;
  font-weight: 700;
`;

export const FormatChoice = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? 'var(--pm-accent-border)' : 'var(--pm-chip-border)')};
  background: ${props => (props.$active ? 'var(--pm-accent-soft)' : 'var(--pm-chip-bg)')};
  color: ${props => (props.$active ? 'var(--pm-accent-text)' : 'var(--pm-chip-text)')};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

export const DownloadPopupActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
`;

export const OverviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

export const OverviewHeaderTitle = styled(InfoTitle)`
  margin: 0;
`;

export const OverviewHeaderAction = styled(ActionButton)`
  width: auto;
  min-width: 120px;
`;
